import "@coreui/coreui/dist/css/coreui.css";
import "@fontsource/inter";
import "@fontsource/chakra-petch/400.css";
import "@fontsource/chakra-petch/500.css";
import "@fontsource/chakra-petch/600.css";
import "@fontsource/chakra-petch/700.css";
import { useRouter } from "next/router";
import type { AppProps } from "next/app";
import React, { Suspense, useEffect } from "react";
import { CSpinner, useColorModes } from "@coreui/react";

import "../styles/style.scss";
import "../styles/tailwind.css";
import { ToastProvider } from "../components/toast/ToastContainer";
import DefaultLayout from "../components/layout/DefaultLayout";
// Containers
// const DefaultLayout = React.lazy(() => import("../components/layout/DefaultLayout"));
import { AuthProvider } from "../auth";

import { GetServerSideProps } from "next";

export default function MBLAB_APP({ Component, pageProps }: AppProps) {
  const router = useRouter();
  // NOTE (Liquid Glass prototype): the hardcoded `setColorMode("light")` effect
  // was removed so the theme isn't forced — the user-menu theme toggle (useTheme)
  // owns light/dark by flipping the `dark` class on <html>. Default to dark on
  // first paint so the glass-on-dark look shows first (useTheme re-applies from
  // localStorage on mount).
  useEffect(() => {
    let theme: "light" | "dark" = "dark";
    try {
      if (window.localStorage.getItem("mb-theme") === "light") theme = "light";
    } catch {
      /* default dark */
    }
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    // Drive CoreUI/Bootstrap nested components too, and seed CoreUI's storage key
    // so useColorModes (in AppSidebar) honors it instead of reverting to light.
    root.setAttribute("data-coreui-theme", theme);
    // Match the mobile browser chrome to the app bg from the first paint.
    const bg = getComputedStyle(root).getPropertyValue("--c-bg").trim();
    if (bg) {
      let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "theme-color";
        document.head.appendChild(meta);
      }
      meta.content = bg;
    }
    try {
      window.localStorage.setItem("coreui-free-react-admin-template-theme", theme);
    } catch {
      /* ignore */
    }
  }, []);

  // Invite activation runs OUTSIDE the app shell. It must be reachable before
  // the user is bound to a record — at that point their token has no app scopes,
  // so DefaultLayout's permission gate would show "NoAccess" and block the
  // accept call. The page drives its own login + redirect.
  const isStandalone = router.pathname.startsWith("/invite");

  return (
    <AuthProvider>
      {isStandalone ? (
        <Component {...pageProps} />
      ) : (
        <DefaultLayout>
          <Suspense
            fallback={
              <div className="pt-3 text-center">
                <CSpinner color="primary" variant="grow" />
              </div>
            }
          >
            <Component {...pageProps} />
          </Suspense>
        </DefaultLayout>
      )}
    </AuthProvider>
  );
}
